<template>
  <div class="p-4 min-h-screen bg-gradient-to-br from-gray-900 to-black">
    <div class="max-w-7xl mx-auto flex flex-col lg:flex-row gap-6">
      <!-- Sidebar -->
      <aside class="w-full lg:w-72 space-y-4">
        <!-- Profile Card -->
        <div class="bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-700 p-4">
          <div class="flex flex-col items-center mb-4">
            <div
                class="w-52 h-52 rounded-xl bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 border border-gray-600 overflow-hidden flex items-center justify-center p-1"
            >
              <v-img
                  v-if="user.profile_picture"
                  class="w-full h-full object-cover rounded-2xl"
                  :src="img_source(user.profile_picture)"
              />
              <span v-else class="text-md text-gray-300">No Image</span>
            </div>
            <span
                class="px-3 py-1 mt-2 rounded-xl text-[11px] bg-gray-800 text-indigo-300 border border-gray-700"
            >
              کد :
              <span class="font-semibold">
                {{ partner ? partner.code : '---' }}
              </span>
            </span>
          </div>

          <div class="space-y-3">
            <input
                v-model="partnerEdit.name"
                type="text"
                :disabled="!isEditingName"
                :class="[
                'w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1',
                isEditingName
                  ? 'border-indigo-500 bg-gray-900 text-gray-200 focus:ring-indigo-500'
                  : 'border-gray-700 bg-gray-900/60 text-gray-400 cursor-not-allowed'
              ]"
            />
            <button
                v-if="!isEditingName"
                @click="toggleEditName"
                class="w-full bg-indigo-600 hover:bg-indigo-700 text-center text-white text-xs font-medium py-2 rounded-lg transition-all"
            >
              ویرایش نام
            </button>
            <button
                v-if="isEditingName"
                @click="updatePartnerName"
                class="w-full bg-green-600 hover:bg-green-700 text-center text-white text-xs font-medium py-2 rounded-lg transition-all"
            >
              ذخیره نام
            </button>

            <div class="text-xs text-gray-400 mt-2 space-y-1 border-t border-gray-700 pt-2">
              <p>
                <span class="font-medium text-gray-300">کاربر:</span>
                <span class="mr-1">{{ partner ? partner.user_name : '-' }}</span>
              </p>
              <p>
                <span class="font-medium text-gray-300">انبار اختصاصی:</span>
                <span class="mr-1">{{ partner ? partner.store_name : '-' }}</span>
              </p>
            </div>
          </div>
        </div>

        <!-- Summary Card -->
        <div
            class="bg-gradient-to-b from-gray-800 to-gray-900 rounded-2xl shadow-lg border border-gray-700 p-4"
        >
          <h3 class="text-xs font-bold text-gray-300 mb-3">خلاصه کل 📊</h3>

          <div class="space-y-3">
            <div class="flex items-center justify-between">
              <div class="text-xs text-gray-400">
                <p class="font-medium text-gray-300">موجودی کل</p>
              </div>
              <div class="text-sm font-bold text-blue-400">
                {{ overallInventorySummary.total_available | toMoney }}
                <span class="text-[8px] font-thin text-gray-500">عدد</span>
              </div>
            </div>

            <div class="flex items-center justify-between">
              <div class="text-xs text-gray-400">
                <p class="font-medium text-gray-300">برابر</p>
              </div>
              <div class="text-sm font-bold text-emerald-400">
                {{ overallInventorySummary.total_value_base | toMoney }}
                <span class="text-[8px] font-thin text-gray-500">تومان</span>
              </div>
            </div>

            <hr class="my-1 border-gray-700" />

            <!-- فروش کل -->
            <div class="flex items-center justify-between">
              <div class="text-xs text-gray-400">
                <p class="font-medium text-gray-300">فروش کل</p>
              </div>
              <div class="text-sm font-bold text-green-400">
                {{ overallSalesSummary.total_revenue_base | toMoney }}
                <span class="text-[8px] font-thin text-gray-500">تومان</span>
              </div>
            </div>

            <!-- سود خالص -->
            <div class="flex items-center justify-between">
              <div class="text-xs text-gray-400">
                <p class="font-medium text-gray-300">سود خالص</p>
              </div>
              <div class="text-sm font-bold text-emerald-400">
                {{ overallSalesSummary.total_profit_base | toMoney }}
                <span class="text-[8px] font-thin text-gray-500">تومان</span>
              </div>
            </div>

            <!-- فروش از پایه -->
            <div class="flex items-center justify-between">
              <div class="text-xs text-gray-400">
                <p class="font-medium text-gray-300">فروش از پایه</p>
              </div>
              <div class="text-sm font-bold text-blue-400">
                {{ (overallSalesSummary.total_revenue_base - overallSalesSummary.total_profit_base) | toMoney }}
                <span class="text-[8px] font-thin text-gray-500">تومان</span>
              </div>
            </div>

            <div class="flex items-center justify-between">
              <div class="text-xs text-gray-400">
                <p class="font-medium text-gray-300">دریافتی از اکسونی‌</p>
              </div>
              <div class="text-sm font-bold text-cyan-400">
                {{ overallPaymentsSummary.total_paid_base | toMoney }}
                <span class="text-[8px] font-thin text-gray-500">تومان</span>
              </div>
            </div>

            <div class="mt-2 p-2 rounded-md bg-gray-800/50 border border-gray-700">
              <div class="flex items-center justify-between">
                <span class="text-xs font-semibold text-gray-300">
                  {{ finalOverallBalanceBase >= 0 ? 'طلب‌کاری' : 'بدهکاری' }}
                </span>
                <span
                    class="text-sm font-bold"
                    :class="finalOverallBalanceBase >= 0 ? 'text-red-400' : 'text-emerald-400'"
                >
                  {{ finalOverallBalanceBase | toMoney }}
                  <span class="text-[8px] font-thin text-gray-500">تومان</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Balance per currency -->
        <div class="bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-700 p-4">
          <h3 class="text-xs font-bold text-gray-300 mb-3">بالانس به تفکیک ارز 💱</h3>

          <div v-if="overallBalancePerCurrency.length" class="space-y-3 max-h-[60vh] overflow-y-auto">
            <div
                v-for="row in overallBalancePerCurrency"
                :key="row.currency_code"
                class="p-2 rounded-xl bg-gray-900/50 border border-gray-700"
            >
              <div class="flex items-center justify-between text-[11px] mb-1">
                <span class="font-semibold text-gray-300">
                  {{ row.currency_title || row.currency_code }}
                </span>
              </div>

              <div class="text-[10px] text-gray-400">
                <p class="mt-2">
                  فروش:
                  {{ row.sold | toMoney }}
                  <span class="text-[8px] font-thin text-gray-300">
                    {{ row.currency_title || row.currency_code }}
                  </span>
                  <span class="text-[7px] font-thin mx-1 text-gray-500">برابر:</span>
                  {{ row.sold_base | toMoney }}
                  <span class="text-[8px] font-thin text-gray-300">تومان</span>
                </p>
                <p class="mt-1">
                  پرداخت:
                  {{ row.paid | toMoney }}
                  <span class="text-[8px] font-thin text-gray-300">
                    {{ row.currency_title || row.currency_code }}
                  </span>
                  <span class="text-[7px] font-thin mx-1 text-gray-500">برابر:</span>
                  {{ row.paid_base | toMoney }}
                  <span class="text-[8px] font-thin text-gray-300">تومان</span>
                </p>

                <p
                    class="p-1 rounded-lg mt-1"
                    :class="row.balance >= 0 ? 'bg-red-900/30 text-red-300' : 'bg-emerald-900/30 text-emerald-300'"
                >
                  {{ row.balance >= 0 ? 'طلبکاری' : 'بدهکاری' }}
                  {{ row.balance | toMoney }}
                  <span class="text-[8px] font-thin text-gray-300">
                    {{ row.currency_title || row.currency_code }}
                  </span>
                  <span class="text-[7px] font-thin mx-1 text-gray-500">برابر:</span>
                  {{ row.balance_base | toMoney }}
                  <span class="text-[8px] font-thin text-gray-300">تومان</span>
                </p>
              </div>
            </div>
          </div>

          <div v-else class="text-xs text-gray-500 text-center py-4">
            داده‌ای وجود ندارد.
          </div>
        </div>
      </aside>

      <!-- Main Content -->
      <main class="flex-1 space-y-4">
        <!-- Date filter -->
        <div class="bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-700 p-4">
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 class="text-sm font-bold text-gray-300 mb-1">فیلتر بازه زمانی</h2>
              <p class="text-[11px] text-gray-400">
                این بازه روی
                <span class="font-semibold text-gray-300">فروش</span>
                و
                <span class="font-semibold text-gray-300">پرداختی‌ها</span>
                اعمال می‌شود.
              </p>
            </div>

            <div class="flex flex-wrap gap-2 text-xs">
              <button
                  v-for="preset in presets"
                  :key="preset.value"
                  @click="setPreset(preset.value)"
                  class="px-3 py-1.5 rounded-full border text-xs transition-all"
                  :class="dateFilter.preset === preset.value
                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                  : 'bg-gray-800 text-gray-300 border-gray-600 hover:bg-gray-700'"
              >
                {{ preset.label }}
              </button>
            </div>
          </div>

          <div v-if="dateFilter.preset === 'custom'" class="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label class="block text-xs text-gray-400 mb-1">از تاریخ</label>
              <input
                  v-model="dateFilter.from"
                  type="date"
                  class="w-full border border-gray-600 bg-gray-900 text-gray-300 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label class="block text-xs text-gray-400 mb-1">تا تاریخ</label>
              <input
                  v-model="dateFilter.to"
                  type="date"
                  class="w-full border border-gray-600 bg-gray-900 text-gray-300 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div class="flex items-end">
              <button
                  @click="applyCustomRange"
                  class="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium py-2 rounded-lg transition-all"
              >
                اعمال فیلتر
              </button>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs mt-4">
            <div class="bg-green-900/30 border border-green-800/50 rounded-xl p-3">
              <p class="text-[11px] text-gray-300 mb-1">تعداد کل فروش</p>
              <p class="text-sm font-bold text-green-400">
                {{ salesSummary.total_quantity | toMoney }}
              </p>
            </div>
            <div class="bg-blue-900/30 border border-blue-800/50 rounded-xl p-3">
              <p class="text-[11px] text-gray-300 mb-1">جمع فروش (تومان)</p>
              <p class="text-sm font-bold text-blue-400">
                {{ salesSummary.total_settlement_base | toMoney }}
              </p>
            </div>
            <div class="bg-purple-900/30 border border-purple-800/50 rounded-xl p-3">
              <p class="text-[11px] text-gray-300 mb-1">فروش حضوری (تومان)</p>
              <p class="text-sm font-bold text-purple-400">
                {{ salesSummary.total_pos_base | toMoney }}
              </p>
            </div>
          </div>
        </div>

        <!-- Sales section -->
        <section class="bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-700 overflow-hidden">
          <header
              class="flex items-center justify-between px-4 py-3 cursor-pointer bg-gray-900/50 hover:bg-gray-800/70 transition"
              @click="collapse.sales = !collapse.sales"
          >
            <div class="flex items-center gap-2">
              <div>
                <h3 class="text-sm font-bold text-gray-300">گزارش فروش محصولات</h3>
                <p class="text-[11px] text-gray-400">
                  بر اساس فروش‌های حضوری و آنلاین در بازه انتخابی
                </p>
              </div>
            </div>
            <button class="text-gray-400 text-lg">
              <i :class="collapse.sales ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
            </button>
          </header>

          <div v-show="collapse.sales" class="p-4 space-y-4">
            <!-- Currency bar chart -->
            <div v-if="salesPerCurrency.length" class="space-y-2">
              <h4 class="text-xs font-semibold text-gray-300 mb-1">
                فروش به تفکیک ارز (تومان)
              </h4>
              <div
                  v-for="row in salesPerCurrency"
                  :key="'sale-cur-' + row.currency_code"
                  class="space-y-1"
              >
                <div class="flex items-center justify-between text-[11px] text-gray-400">
                  <span>{{ row.currency_title || row.currency_code }}</span>
                  <span>{{ Number(row.total_revenue_base || 0) | toMoney }} تومان</span>
                </div>
                <div class="w-full h-2 rounded-full bg-gray-700 overflow-hidden">
                  <div
                      class="h-2 rounded-full bg-gradient-to-l from-green-500 to-emerald-500"
                      :style="{ width: salesCurrencyBarWidth(row) }"
                  ></div>
                </div>
              </div>
            </div>

            <!-- Product sales table -->
            <div class="mt-3 overflow-x-auto">
              <table class="w-full text-[11px]">
                <thead class="bg-gray-900">
                <tr>
                  <th class="py-2 px-2 text-right text-gray-300 border-b border-gray-700">کالا</th>
                  <th class="py-2 px-2 text-center text-gray-300 border-b border-gray-700">تعداد</th>
                  <th class="py-2 px-2 text-center text-gray-300 border-b border-gray-700">مبلغ فروش (ارز)</th>
                  <th class="py-2 px-2 text-center text-gray-300 border-b border-gray-700">مبلغ فروش (تومان)</th>
                </tr>
                </thead>
                <tbody class="divide-y divide-gray-700">
                <tr
                    v-for="item in salesPerProduct"
                    :key="'sale-prod-' + item.product_id"
                    class="hover:bg-gray-800/50 transition"
                >
                  <td class="py-2 px-2 text-right font-medium text-gray-200">
                    {{ item.product_name }}
                  </td>
                  <td class="py-2 px-2 text-center text-gray-300">
                    {{ item.total_quantity | toMoney }}
                  </td>
                  <td class="py-2 px-2 text-center text-gray-300">
                    {{ Number(item.total_revenue || 0) | toMoney }}
                  </td>
                  <td class="py-2 px-2 text-center text-gray-300">
                    {{ Number(item.total_revenue_base || 0) | toMoney }}
                  </td>
                </tr>
                <tr v-if="!salesPerProduct.length">
                  <td colspan="4" class="py-4 text-center text-gray-500">
                    در این بازه زمانی فروشی ثبت نشده است.
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <!-- Inventory section -->
        <section class="bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-700 overflow-hidden">
          <header
              class="flex items-center justify-between px-4 py-3 cursor-pointer bg-gray-900/50 hover:bg-gray-800/70 transition"
              @click="collapse.inventory = !collapse.inventory"
          >
            <div class="flex items-center gap-2">
              <div>
                <h3 class="text-sm font-bold text-gray-300">گزارش موجودی کالاها</h3>
                <p class="text-[11px] text-gray-400">
                  بر اساس موجودی فعلی و قیمت واحد
                </p>
              </div>
            </div>
            <button class="text-gray-400 text-lg">
              <i :class="collapse.inventory ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
            </button>
          </header>

          <div v-show="collapse.inventory" class="p-4 space-y-4">
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
              <div class="bg-blue-900/30 border border-blue-800/50 rounded-xl p-3">
                <p class="text-[11px] text-gray-300 mb-1">کل موجودی (تعداد)</p>
                <p class="text-sm font-bold text-blue-400">
                  {{ inventorySummary.total_available | toMoney }}
                </p>
              </div>
              <div class="bg-emerald-900/30 border border-emerald-800/50 rounded-xl p-3">
                <p class="text-[11px] text-gray-300 mb-1">ارزش موجودی (تومان)</p>
                <p class="text-sm font-bold text-emerald-400">
                  {{ inventorySummary.total_value_base | toMoney }}
                </p>
              </div>
              <div class="bg-gray-800 border border-gray-700 rounded-xl p-3">
                <p class="text-[11px] text-gray-300 mb-1">تعداد ارزها</p>
                <p class="text-sm font-bold text-gray-300">
                  {{ inventorySummary.per_currency.length | toMoney }}
                </p>
              </div>
            </div>

            <!-- Currency bar chart -->
            <div v-if="inventorySummary.per_currency.length" class="space-y-2">
              <h4 class="text-xs font-semibold text-gray-300 mb-1">ارزش موجودی به تفکیک ارز</h4>
              <div
                  v-for="row in inventorySummary.per_currency"
                  :key="'inv-cur-' + row.currency_code"
                  class="space-y-1"
              >
                <div class="flex items-center justify-between text-[11px] text-gray-400">
                  <span>{{ row.currency_title || row.currency_code }}</span>
                  <span>{{ Number(row.total_value_base || 0) | toMoney }} تومان</span>
                </div>
                <div class="w-full h-2 rounded-full bg-gray-700 overflow-hidden">
                  <div
                      class="h-2 rounded-full bg-gradient-to-l from-blue-500 to-indigo-500"
                      :style="{ width: inventoryCurrencyBarWidth(row) }"
                  ></div>
                </div>
              </div>
            </div>

            <!-- Inventory table -->
            <div class="mt-3 overflow-x-auto">
              <table class="w-full text-[11px]">
                <thead class="bg-gray-900">
                <tr>
                  <th class="py-2 px-2 text-right text-gray-300 border-b border-gray-700">کالا</th>
                  <th class="py-2 px-2 text-center text-gray-300 border-b border-gray-700">موجودی</th>
                  <th class="py-2 px-2 text-center text-gray-300 border-b border-gray-700">قیمت واحد</th>
                  <th class="py-2 px-2 text-center text-gray-300 border-b border-gray-700">ارز</th>
                  <th class="py-2 px-2 text-center text-gray-300 border-b border-gray-700">ارزش کل (ارز)</th>
                  <th class="py-2 px-2 text-center text-gray-300 border-b border-gray-700">ارزش کل (تومان)</th>
                </tr>
                </thead>
                <tbody class="divide-y divide-gray-700">
                <tr
                    v-for="(item, index) in inventoryItems"
                    :key="'inv-item-' + index"
                    class="hover:bg-gray-800/50 transition"
                >
                  <td class="py-2 px-2 text-right font-medium text-gray-200">
                    {{ item.product_name }}
                  </td>
                  <td class="py-2 px-2 text-center text-gray-300">
                    {{ item.inventory_quantity | toMoney }}
                  </td>
                  <td class="py-2 px-2 text-center text-gray-300">
                    {{ Number(item.unit_price || 0) | toMoney }}
                  </td>
                  <td class="py-2 px-2 text-center text-gray-300">
                    {{ item.currency_title || item.currency_code || '-' }}
                  </td>
                  <td class="py-2 px-2 text-center text-gray-300">
                    {{ Number(item.total_value || 0) | toMoney }}
                  </td>
                  <td class="py-2 px-2 text-center text-gray-300">
                    {{ Number(item.total_value_base || 0) | toMoney }}
                  </td>
                </tr>
                <tr v-if="!inventoryItems.length">
                  <td colspan="6" class="py-4 text-center text-gray-500">
                    داده‌ای برای موجودی این شریک ثبت نشده است.
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <!-- Payments section -->
        <section class="bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-700 overflow-hidden">
          <header
              class="flex items-center justify-between px-4 py-3 cursor-pointer bg-gray-900/50 hover:bg-gray-800/70 transition"
              @click="collapse.payments = !collapse.payments"
          >
            <div class="flex items-center gap-2">
              <div>
                <h3 class="text-sm font-bold text-gray-300">گزارش پرداختی‌ها (نقدی + چک)</h3>
                <p class="text-[11px] text-gray-400">
                  مجموع پرداخت‌ها به شریک در بازه انتخابی
                </p>
              </div>
            </div>
            <button class="text-gray-400 text-lg">
              <i :class="collapse.payments ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
            </button>
          </header>

          <div v-show="collapse.payments" class="p-4 space-y-4">
            <!-- Summary mini cards -->
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
              <div class="bg-amber-900/30 border border-amber-800/50 rounded-xl p-3">
                <p class="text-[11px] text-gray-300 mb-1">جمع پرداخت نقدی (تومان)</p>
                <p class="text-sm font-bold text-amber-400">
                  {{ paymentsSummary.cash_base | toMoney }}
                </p>
              </div>
              <div class="bg-sky-900/30 border border-sky-800/50 rounded-xl p-3">
                <p class="text-[11px] text-gray-300 mb-1">جمع چک‌ها (تومان)</p>
                <p class="text-sm font-bold text-sky-400">
                  {{ paymentsSummary.check_base | toMoney }}
                </p>
              </div>
              <div class="bg-rose-900/30 border border-rose-800/50 rounded-xl p-3">
                <p class="text-[11px] text-gray-300 mb-1">جمع کل پرداختی‌ها (تومان)</p>
                <p class="text-sm font-bold text-rose-400">
                  {{ paymentsSummary.total_paid_base | toMoney }}
                </p>
              </div>
            </div>

            <!-- Payments per currency chart -->
            <div v-if="paymentsTotalPerCurrency.length" class="space-y-2">
              <h4 class="text-xs font-semibold text-gray-300 mb-1">
                پرداختی‌ها به تفکیک ارز (تومان)
              </h4>
              <div
                  v-for="row in paymentsTotalPerCurrency"
                  :key="'pay-cur-' + row.currency_code"
                  class="space-y-1"
              >
                <div class="flex items-center justify-between text-[11px] text-gray-400">
                  <span>{{ row.currency_title || row.currency_code }}</span>
                  <span>{{ Number(row.total_amount_base || 0) | toMoney }} تومان</span>
                </div>
                <div class="w-full h-2 rounded-full bg-gray-700 overflow-hidden">
                  <div
                      class="h-2 rounded-full bg-gradient-to-l from-rose-500 to-orange-500"
                      :style="{ width: paymentsCurrencyBarWidth(row) }"
                  ></div>
                </div>
              </div>
            </div>

            <!-- Cash table -->
            <div class="mt-3">
              <h4 class="text-xs font-semibold text-gray-300 mb-2">پرداخت‌های نقدی</h4>
              <div class="overflow-x-auto">
                <table class="w-full text-[11px]">
                  <thead class="bg-gray-900">
                  <tr>
                    <th class="py-2 px-2 text-center text-gray-300 border-b border-gray-700">مبلغ</th>
                    <th class="py-2 px-2 text-center text-gray-300 border-b border-gray-700">ارز</th>
                    <th class="py-2 px-2 text-center text-gray-300 border-b border-gray-700">معادل تومان</th>
                    <th class="py-2 px-2 text-center text-gray-300 border-b border-gray-700">تاریخ پرداخت</th>
                    <th class="py-2 px-2 text-right text-gray-300 border-b border-gray-700">توضیحات</th>
                  </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-700">
                  <tr
                      v-for="item in paymentsCashItems"
                      :key="'cash-' + item.id"
                      class="hover:bg-gray-800/50 transition"
                  >
                    <td class="py-2 px-2 text-center text-gray-300">
                      {{ Number(item.amount || 0) | toMoney }}
                    </td>
                    <td class="py-2 px-2 text-center text-gray-300">
                      {{ item.currency_title || item.currency_code || '-' }}
                    </td>
                    <td class="py-2 px-2 text-center text-gray-300">
                      {{ Number(item.amount_base || 0) | toMoney }}
                    </td>
                    <td class="py-2 px-2 text-center text-gray-300">
                      {{ item.paid_at }}
                    </td>
                    <td class="py-2 px-2 text-right text-gray-400">
                      {{ item.note || '-' }}
                    </td>
                  </tr>
                  <tr v-if="!paymentsCashItems.length">
                    <td colspan="5" class="py-4 text-center text-gray-500">
                      پرداخت نقدی برای این بازه ثبت نشده است.
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Check table -->
            <div class="mt-3">
              <h4 class="text-xs font-semibold text-gray-300 mb-2">چک‌ها</h4>
              <div class="overflow-x-auto">
                <table class="w-full text-[11px]">
                  <thead class="bg-gray-900">
                  <tr>
                    <th class="py-2 px-2 text-center text-gray-300 border-b border-gray-700">مبلغ</th>
                    <th class="py-2 px-2 text-center text-gray-300 border-b border-gray-700">تاریخ ثبت</th>
                    <th class="py-2 px-2 text-center text-gray-300 border-b border-gray-700">سررسید</th>
                    <th class="py-2 px-2 text-right text-gray-300 border-b border-gray-700">توضیحات</th>
                  </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-700">
                  <tr
                      v-for="item in paymentsCheckItems"
                      :key="'check-' + item.id"
                      class="hover:bg-gray-800/50 transition"
                  >
                    <td class="py-2 px-2 text-center text-gray-300">
                      {{ Number(item.amount || 0) | toMoney }}
                    </td>
                    <td class="py-2 px-2 text-center text-gray-300">
                      {{ item.issue_date }}
                    </td>
                    <td class="py-2 px-2 text-center text-gray-300">
                      {{ item.due_date }}
                    </td>
                    <td class="py-2 px-2 text-right text-gray-400">
                      {{ item.note || '-' }}
                    </td>
                  </tr>
                  <tr v-if="!paymentsCheckItems.length">
                    <td colspan="4" class="py-4 text-center text-gray-500">
                      چکی برای این بازه ثبت نشده است.
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        <div v-if="globalLoading" class="text-center text-xs text-gray-400 py-4">
          در حال بارگذاری داده‌های شریک...
        </div>
        <div v-if="error" class="text-center text-xs text-red-400 py-4">
          {{ error }}
        </div>
      </main>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PartnerDashboard',
  data () {
    return {
      overallInventorySummary: {
        total_available: 0,
        total_value_base: 0,
      },
      overallSalesSummary: {
        total_quantity: 0,
        total_settlement_amount_base: 0,
        total_revenue_base: 0,
        total_profit_base: 0,
        total_pos_revenue_base: 0,
        total_shop_revenue_base: 0,
        total_pos_profit_base: 0,
        total_shop_profit_base: 0,
      },
      overallPaymentsSummary: {
        total_paid_base: 0,
      },
      _overallSalesPerCurrency: [],
      _overallPaymentsPerCurrency: [],
      overallBalancePerCurrency: [],
      partner: null,
      partnerEdit: {
        name: ''
      },
      isEditingName: false,
      inventoryData: null,
      salesData: null,
      paymentsData: null,

      loading: {
        partner: false,
        inventory: false,
        sales: false,
        payments: false
      },
      error: null,

      dateFilter: {
        preset: 'all',
        from: '',
        to: ''
      },
      currentRange: {
        from: '',
        to: ''
      },
      presets: [
        { value: 'all', label: 'کلی' },
        { value: 'today', label: 'امروز' },
        { value: 'yesterday', label: 'دیروز' },
        { value: 'this_month', label: 'ماه جاری' },
        { value: 'last_month', label: 'ماه قبل' },
        { value: 'custom', label: 'بازه دلخواه' }
      ],
      collapse: {
        inventory: false,
        sales: false,
        payments: false
      }
    }
  },
  computed: {
    finalOverallBalanceBase() {
      const sold = Number(this.overallSalesSummary?.total_settlement_amount_base || 0)
      const paid = Number(this.overallPaymentsSummary?.total_paid_base || 0)
      return sold - paid
    },
    globalLoading () {
      return this.loading.partner || this.loading.inventory || this.loading.sales || this.loading.payments
    },

    // Inventory
    inventorySummary () {
      var s = (this.inventoryData && this.inventoryData.summary) || {}
      return {
        total_available: s.total_quantity || s.total_available || 0,
        total_value_base: Number(s.total_value_base || 0),
        per_currency: s.per_currency || []
      }
    },
    inventoryItems () {
      return (this.inventoryData && this.inventoryData.items) || []
    },
    maxInventoryValueBase () {
      var arr = this.inventorySummary.per_currency || []
      if (!arr.length) return 1
      var max = 0
      arr.forEach(function (c) {
        var v = Number(c.total_value_base || 0) || 0
        if (v > max) max = v
      })
      return max || 1
    },

    // Sales
    salesSummary () {
      var s = (this.salesData && this.salesData.summary) || {}
      return {
        total_quantity: s.total_quantity || 0,
        total_settlement_base: Number(s.total_revenue_base || s.total_settlement_amount_base || 0),
        total_pos_base: Number(s.total_pos_revenue_base || 0)
      }
    },
    salesPerCurrency () {
      return (this.salesData && this.salesData.per_currency) || []
    },
    salesPerProduct () {
      return (this.salesData && this.salesData.per_product) || []
    },
    maxSalesValueBase () {
      var arr = this.salesPerCurrency || []
      if (!arr.length) return 1
      var max = 0
      arr.forEach(function (c) {
        var v = Number(c.total_revenue_base || 0) || 0
        if (v > max) max = v
      })
      return max || 1
    },

    // Payments
    paymentsSummary () {
      var s = (this.paymentsData && this.paymentsData.summary) || {}
      var cashBase = Number(((s.cash || {}).total_base) || 0)
      var checkBase = Number(((s.check || {}).total_base) || 0)
      var totalBase = Number(((s.total || {}).total_base) || 0)
      return {
        cash_base: cashBase,
        check_base: checkBase,
        total_paid_base: totalBase
      }
    },
    paymentsTotalPerCurrency () {
      var s = (this.paymentsData && this.paymentsData.summary) || {}
      var total = s.total || {}
      return total.per_currency || []
    },
    paymentsCashItems () {
      return (this.paymentsData && this.paymentsData.cash_items) || []
    },
    paymentsCheckItems () {
      return (this.paymentsData && this.paymentsData.check_items) || []
    },
    maxPaymentsValueBase () {
      var arr = this.paymentsTotalPerCurrency || []
      if (!arr.length) return 1
      var max = 0
      arr.forEach(function (c) {
        var v = Number(c.total_amount_base || 0) || 0
        if (v > max) max = v
      })
      return max || 1
    },

    // Balance per currency (برای بازه انتخابی)
    balancePerCurrency () {
      var res = []
      var salesMap = {}
      var payMap = {}
      var i

      var salesArr = this.salesPerCurrency || []
      for (i = 0; i < salesArr.length; i++) {
        var cs = salesArr[i]
        var csCode = cs.currency_code || 'IRR'
        salesMap[csCode] = cs
      }

      var payArr = this.paymentsTotalPerCurrency || []
      for (i = 0; i < payArr.length; i++) {
        var cp = payArr[i]
        var cpCode = cp.currency_code || 'IRR'
        payMap[cpCode] = cp
      }

      var codesObj = {}
      Object.keys(salesMap).forEach(function (code) { codesObj[code] = true })
      Object.keys(payMap).forEach(function (code) { codesObj[code] = true })

      Object.keys(codesObj).forEach(function (code) {
        var s = salesMap[code] || {}
        var p = payMap[code] || {}
        var title = s.currency_title || p.currency_title || code

        var sold = Number(s.total_revenue || 0)
        var soldBase = Number(s.total_revenue_base || 0)
        var paid = Number(p.total_amount || 0)
        var paidBase = Number(p.total_amount_base || 0)

        res.push({
          currency_code: code,
          currency_title: title,
          sold: sold,
          sold_base: soldBase,
          paid: paid,
          paid_base: paidBase,
          balance: sold - paid,
          balance_base: soldBase - paidBase
        })
      })

      return res
    },

    finalBalanceBase () {
      return this.salesSummary.total_settlement_base - this.paymentsSummary.total_paid_base
    }
  },
  methods: {
    buildOverallBalancePerCurrency() {
      if (!this._overallSalesPerCurrency || !this._overallPaymentsPerCurrency) return

      const salesMap = {}
      this._overallSalesPerCurrency.forEach((row) => {
        salesMap[row.currency_code] = row
      })

      const paysMap = {}
      this._overallPaymentsPerCurrency.forEach((row) => {
        paysMap[row.currency_code] = row
      })

      const codes = new Set([
        ...Object.keys(salesMap),
        ...Object.keys(paysMap),
      ])

      const result = []
      codes.forEach((code) => {
        const s = salesMap[code] || {}
        const p = paysMap[code] || {}

        const currency_title = s.currency_title || p.currency_title || code

        const sold = Number(s.total_revenue || 0)
        const sold_base = Number(s.total_revenue_base || 0)

        const paid = Number(p.total_amount || 0)
        const paid_base = Number(p.total_amount_base || 0)

        result.push({
          currency_code: code,
          currency_title,
          sold,
          sold_base,
          paid,
          paid_base,
          balance_base: sold_base - paid_base,
          balance: sold - paid,
        })
      })

      this.overallBalancePerCurrency = result
    },

    fetchOverallSummary() {
      if (!this.partner?.id) return
      const pid = this.partner.id

      // موجودی کلی
      this.request({
        url: this.endpoint(`partner-stock/dashboard/partners/${pid}/inventory/`),
        method: 'get',
        success: (res) => {
          const s = res.summary || {}
          this.overallInventorySummary = {
            total_available: s.total_quantity || 0,
            total_value_base: Number(s.total_value_base || 0),
          }
        },
      })

// فروش کلی
      this.request({
        url: this.endpoint(`partner-stock/dashboard/partners/${pid}/sales/`),
        method: 'get',
        params: { scope: 'all' },
        success: (res) => {
          const s = res.summary || {}

          this.overallSalesSummary = {
            total_quantity: s.total_quantity || 0,

            // فروش کل
            total_settlement_amount_base: Number(s.total_revenue_base || 0), // برای استفاده‌های قبلی
            total_revenue_base: Number(s.total_revenue_base || 0),

            // سود
            total_profit_base: Number(s.total_profit_base || 0),

            // تفکیک حضوری / آنلاین
            total_pos_revenue_base: Number(s.total_pos_revenue_base || 0),
            total_shop_revenue_base: Number(s.total_shop_revenue_base || 0),
            total_pos_profit_base: Number(s.total_pos_profit_base || 0),
            total_shop_profit_base: Number(s.total_shop_profit_base || 0),
          }

          this._overallSalesPerCurrency = res.per_currency || []
          this.buildOverallBalancePerCurrency()
        },
      })

      // پرداختی کلی
      this.request({
        url: this.endpoint(`partner-stock/dashboard/partners/${pid}/payments/`),
        method: 'get',
        params: { scope: 'all' },
        success: (res) => {
          const totalBase = res.summary?.total?.total_base || 0
          this.overallPaymentsSummary = {
            total_paid_base: Number(totalBase || 0),
          }
          this._overallPaymentsPerCurrency = res.summary?.total?.per_currency || []
          this.buildOverallBalancePerCurrency()
        },
      })
    },
    pad (n) {
      return n < 10 ? '0' + n : '' + n
    },
    formatDate (d) {
      return d.getFullYear() + '-' + this.pad(d.getMonth() + 1) + '-' + this.pad(d.getDate())
    },
    computeDateRange (preset) {
      var today = new Date()
      var from, to

      if (preset === 'all') {
        from = '2020-01-01'
        to = '2090-12-31'
      } else if (preset === 'today') {
        from = to = this.formatDate(today)
      } else if (preset === 'yesterday') {
        var y = new Date(today)
        y.setDate(y.getDate() - 1)
        from = to = this.formatDate(y)
      } else if (preset === 'this_month') {
        var first = new Date(today.getFullYear(), today.getMonth(), 1)
        var last = new Date(today.getFullYear(), today.getMonth() + 1, 0)
        from = this.formatDate(first)
        to = this.formatDate(last)
      } else if (preset === 'last_month') {
        var first2 = new Date(today.getFullYear(), today.getMonth() - 1, 1)
        var last2 = new Date(today.getFullYear(), today.getMonth(), 0)
        from = this.formatDate(first2)
        to = this.formatDate(last2)
      } else {
        from = this.dateFilter.from
        to = this.dateFilter.to
      }

      return { from: from, to: to }
    },

    setPreset (preset) {
      this.dateFilter.preset = preset
      if (preset === 'custom') return
      var range = this.computeDateRange(preset)
      this.currentRange = range
      this.fetchSalesAndPayments()
    },
    applyCustomRange () {
      if (!this.dateFilter.from || !this.dateFilter.to) {
        if (this.$toast && this.$toast.error) {
          this.$toast.error('لطفاً بازه تاریخ را کامل وارد کنید.')
        }
        return
      }
      this.currentRange = {
        from: this.dateFilter.from,
        to: this.dateFilter.to
      }
      this.fetchSalesAndPayments()
    },

    inventoryCurrencyBarWidth (row) {
      var max = this.maxInventoryValueBase
      var val = Number(row.total_value_base || 0)
      var pct = max ? (val / max) * 100 : 0
      if (pct < 5 && val > 0) pct = 5
      return pct + '%'
    },
    salesCurrencyBarWidth (row) {
      var max = this.maxSalesValueBase
      var val = Number(row.total_revenue_base || 0)
      var pct = max ? (val / max) * 100 : 0
      if (pct < 5 && val > 0) pct = 5
      return pct + '%'
    },
    paymentsCurrencyBarWidth (row) {
      var max = this.maxPaymentsValueBase
      var val = Number(row.total_amount_base || 0)
      var pct = max ? (val / max) * 100 : 0
      if (pct < 5 && val > 0) pct = 5
      return pct + '%'
    },

    fetchPartner () {
      console.log('test')
      var self = this
      this.loading.partner = true
      this.error = null

      this.request({
        url: this.endpoint('partner-stock/partner-by-user/' + this.user.id + '/'),
        method: 'get',
        success: function (data) {
          self.partner = data
          self.partnerEdit.name = data.name
          self.loading.partner = false
          self.fetchInventory()
          self.setPreset('today')
          self.fetchOverallSummary()
        },
        error: function () {
          self.loading.partner = false
          self.error = 'خطا در دریافت اطلاعات شریک.'
        }
      })
    },
    fetchInventory () {
      if (!this.partner || !this.partner.id) return
      var self = this
      this.loading.inventory = true

      this.request({
        url: this.endpoint('partner-stock/dashboard/partners/' + this.partner.id + '/inventory/'),
        method: 'get',
        success: function (data) {
          self.inventoryData = data
          self.loading.inventory = false
        },
        error: function () {
          self.loading.inventory = false
          self.error = 'خطا در دریافت گزارش موجودی.'
        }
      })
    },
    fetchSalesAndPayments () {
      if (!this.partner || !this.partner.id) return
      var from = this.currentRange.from
      var to = this.currentRange.to
      if (!from || !to) return

      var self = this

      // Sales
      this.loading.sales = true
      this.request({
        url: this.endpoint('partner-stock/dashboard/partners/' + this.partner.id + '/sales/?from=' + from + '&to=' + to),
        method: 'get',
        success: function (data) {
          self.salesData = data
          self.loading.sales = false
        },
        error: function () {
          self.loading.sales = false
          self.error = 'خطا در دریافت گزارش فروش.'
        }
      })

      // Payments
      this.loading.payments = true
      this.request({
        url: this.endpoint('partner-stock/dashboard/partners/' + this.partner.id + '/payments/?from=' + from + '&to=' + to),
        method: 'get',
        success: function (data) {
          self.paymentsData = data
          self.loading.payments = false
        },
        error: function () {
          self.loading.payments = false
          self.error = 'خطا در دریافت گزارش پرداخت‌ها.'
        }
      })
    },

    toggleEditName() {
      if (!this.isEditingName && this.partner) {
        this.partnerEdit.name = this.partner.name
      }
      this.isEditingName = !this.isEditingName
    },

    updatePartnerName() {
      if (!this.partner || !this.partnerEdit.name) {
        return this.$toast?.error?.('نام شریک را وارد کنید')
      }

      this.request({
        url: this.endpoint(`partner-stock/partners/${this.partner.id}/`),
        method: 'patch',
        data: {
          name: this.partnerEdit.name,
        },
        success: (res) => {
          this.partner = res
          this.isEditingName = false
          this.successNotify('نام شریک با موفقیت ذخیره شد')
        },
      })
    },
  },
  watch: {
    userId: function (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.fetchPartner()
      }
    }
  },
  mounted () {
    this.fetchPartner()
  }
}
</script>

<style scoped>
aside .max-h-\[60vh\] {
  scrollbar-width: thin;
  scrollbar-color: rgba(75, 85, 99, 0.8) rgba(31, 41, 55, 0.5);
}
aside .max-h-\[60vh\]::-webkit-scrollbar {
  width: 6px;
}
aside .max-h-\[60vh\]::-webkit-scrollbar-track {
  background: rgba(31, 41, 55, 0.5);
  border-radius: 3px;
}
aside .max-h-\[60vh\]::-webkit-scrollbar-thumb {
  background-color: rgba(75, 85, 99, 0.8);
  border-radius: 3px;
}
aside .max-h-\[60vh\]::-webkit-scrollbar-thumb:hover {
  background-color: rgba(107, 114, 128, 0.9);
}

.glass-effect {
  background: rgba(30, 41, 59, 0.7);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

header {
  transition: background-color 0.3s ease;
}

table {
  border-collapse: separate;
  border-spacing: 0;
}

th {
  font-weight: 600;
}

td, th {
  border-color: rgba(55, 65, 81, 0.7);
}

/* گرادینت متن */
.text-gradient {
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
</style>